import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Package,
  FileText,
  TestTube,
  Calculator,
  Clock,
  Star,
  Target,
  Upload,
  X,
  Image,
  Paperclip
} from 'lucide-react';
import { toast } from 'sonner';
import { useProcurement } from '@/context/ProcurementContext';
import { formatRating, formatCurrency, removeTrailingZeroes } from '@/lib/utils';

interface IQCTestingModalProps {
  po: any;
  isOpen: boolean;
  onClose: () => void;
  onComplete: (poId: number, testResults: any) => Promise<void>;
  currentUser: any;
}

interface IQCSample {
  itemId: number;
  skuId: number;
  itemName: string;
  skuCode: string;
  poQuantity: number;
  sampleSize: number;
  rating: number;
  notes: string;
  defects: string[];
  attachments: string[];
}

export const IQCTestingModal: React.FC<IQCTestingModalProps> = ({
  po,
  isOpen,
  onClose,
  onComplete,
  currentUser
}) => {
  const { state } = useProcurement();
  const [samples, setSamples] = useState<IQCSample[]>([]);
  const [overallRating, setOverallRating] = useState<number>(0);
  const [overallNotes, setOverallNotes] = useState('');
  const [overallAttachments, setOverallAttachments] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [timer, setTimer] = useState<number>(0);
  const [isExpired, setIsExpired] = useState(false);

  // Initialize samples from PO items
  useEffect(() => {
    if (po && po.items) {
      const initialSamples = po.items.map((item: any, index: number) => ({
        itemId: item.id || index + 1,
        skuId: item.skuId,
        itemName: item.itemName,
        skuCode: item.skuCode,
        poQuantity: item.quantity,
        sampleSize: Math.max(1, Math.ceil(item.quantity * 0.1)), // 10% sample size (minimum 1)
        rating: 0,
        notes: '',
        defects: [],
        attachments: []
      }));
      setSamples(initialSamples);
    }
  }, [po]);

  // Calculate overall rating based on sample ratings
  useEffect(() => {
    const validRatings = samples.filter(s => s.rating > 0);
    if (validRatings.length > 0) {
      const average = validRatings.reduce((sum, s) => sum + s.rating, 0) / validRatings.length;
      setOverallRating(Math.round(average * 100) / 100); // Round to 2 decimal places
    } else {
      setOverallRating(0);
    }
  }, [samples]);

  // Timer logic (24 hours from PO creation)
  useEffect(() => {
    if (po && po.createdAt) {
      const startTime = new Date(po.createdAt).getTime();
      const now = Date.now();
      const elapsed = Math.floor((now - startTime) / 1000); // seconds
      const remaining = Math.max(0, 24 * 60 * 60 - elapsed); // 24 hours in seconds
      
      setTimer(remaining);
      setIsExpired(remaining === 0);

      const interval = setInterval(() => {
        setTimer(prev => {
          if (prev <= 1) {
            setIsExpired(true);
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [po]);

  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusFromRating = (rating: number): { status: string; color: string; icon: React.ReactNode } => {
    if (rating >= 5.0) {
      return { 
        status: 'Fully Accepted', 
        color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
        icon: <CheckCircle className="h-4 w-4" />
      };
    } else if (rating >= 3.0) {
      return { 
        status: 'Partly Accepted', 
        color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
        icon: <AlertTriangle className="h-4 w-4" />
      };
    } else {
      return { 
        status: 'Fully Rejected', 
        color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
        icon: <XCircle className="h-4 w-4" />
      };
    }
  };

  const updateSampleRating = (index: number, rating: number) => {
    setSamples(prev => prev.map((sample, i) => 
      i === index ? { ...sample, rating } : sample
    ));
  };

  const updateSampleNotes = (index: number, notes: string) => {
    setSamples(prev => prev.map((sample, i) => 
      i === index ? { ...sample, notes } : sample
    ));
  };

  const updateSampleSize = (index: number, sampleSize: number) => {
    setSamples(prev => prev.map((sample, i) => 
      i === index ? { ...sample, sampleSize } : sample
    ));
  };

  const handleSampleFileUpload = (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const fileNames = Array.from(files).map(file => file.name);
      setSamples(prev => prev.map((sample, i) => 
        i === index ? { ...sample, attachments: [...sample.attachments, ...fileNames] } : sample
      ));
    }
  };

  const handleOverallFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const fileNames = Array.from(files).map(file => file.name);
      setOverallAttachments(prev => [...prev, ...fileNames]);
    }
  };

  const removeSampleAttachment = (sampleIndex: number, attachmentIndex: number) => {
    setSamples(prev => prev.map((sample, i) => 
      i === sampleIndex 
        ? { ...sample, attachments: sample.attachments.filter((_, idx) => idx !== attachmentIndex) }
        : sample
    ));
  };

  const removeOverallAttachment = (attachmentIndex: number) => {
    setOverallAttachments(prev => prev.filter((_, idx) => idx !== attachmentIndex));
  };

  const handleSubmit = async () => {
    if (samples.some(s => s.rating === 0 || s.rating === undefined || s.rating === null)) {
      toast.error('Please enter a rating (0.0 - 5.0) for all items before submitting');
      return;
    }

    if (samples.some(s => s.rating < 0 || s.rating > 5)) {
      toast.error('All ratings must be between 0.0 and 5.0');
      return;
    }

    if (samples.some(s => s.sampleSize < 1 || s.sampleSize > s.poQuantity)) {
      toast.error('Sample sizes must be between 1 and the total PO quantity for each item');
      return;
    }

    setIsSubmitting(true);
    try {
      const testResults = {
        samples,
        overallRating,
        overallNotes,
        overallAttachments,
        testedBy: currentUser?.id || 1,
        testedByName: currentUser?.name || 'Unknown User',
        testedAt: new Date().toISOString(),
        isExpired,
        timerRemaining: timer
      };
      
      await onComplete(po.id, testResults);
      toast.success('IQC testing completed successfully');
      onClose();
    } catch (error) {
      console.error('Failed to complete IQC testing:', error);
      toast.error('Failed to complete IQC testing');
    } finally {
      setIsSubmitting(false);
    }
  };

  const statusInfo = getStatusFromRating(overallRating);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl h-[90vh] p-0 overflow-hidden">
        <div className="flex flex-col h-full min-h-0">
        <div className="shrink-0 sticky top-0 z-10 bg-background border-b p-4">
          <div className="flex items-start justify-between">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <TestTube className="h-5 w-5" />
                IQC Testing - {po?.poNumber}
              </DialogTitle>
              <DialogDescription>
                Incoming Quality Control testing using sampling methodology
              </DialogDescription>
            </DialogHeader>
            <button
              aria-label="Close"
              onClick={onClose}
              className="ml-4 inline-flex h-8 w-8 items-center justify-center rounded-md border hover:bg-muted"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
        {/* Timer Display */}
        <Card className={`${isExpired ? 'border-red-200 bg-red-50 dark:bg-red-950' : 'border-blue-200 bg-blue-50 dark:bg-blue-950'}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className={`h-5 w-5 ${isExpired ? 'text-red-600' : 'text-blue-600'}`} />
                <span className="font-semibold">
                  {isExpired ? 'Timer Expired' : 'IQC Timer'}
                </span>
              </div>
              <div className="text-2xl font-mono font-bold">
                {formatTime(timer)}
              </div>
            </div>
            {isExpired && (
              <Alert className="mt-2">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  IQC testing timer has expired! Please complete testing immediately or notify manager.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* PO Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Purchase Order Details
              </CardTitle>
            </CardHeader>
            <CardContent>
            <div className="grid grid-cols-2 gap-4">
                <div>
                <Label className="text-sm font-medium">PO Number</Label>
                <p className="text-sm text-muted-foreground">{po?.poNumber}</p>
                </div>
                <div>
                <Label className="text-sm font-medium">Vendor</Label>
                <p className="text-sm text-muted-foreground">{po?.vendorName}</p>
                </div>
                <div>
                <Label className="text-sm font-medium">Total Amount</Label>
                <p className="text-sm text-muted-foreground">{formatCurrency(po?.totalAmount || 0)}</p>
                </div>
                <div>
                <Label className="text-sm font-medium">Created</Label>
                <p className="text-sm text-muted-foreground">
                  {po?.createdAt ? new Date(po.createdAt).toLocaleDateString() : 'N/A'}
                </p>
              </div>
              </div>
            </CardContent>
          </Card>

        {/* Sample Testing */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Target className="h-5 w-5" />
            Sample Testing (Rate each sample 0-5)
          </h3>
          
          {samples.map((sample, index) => (
            <Card key={index}>
              <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div>
                    <CardTitle className="text-base">{sample.itemName}</CardTitle>
                    <p className="text-sm text-muted-foreground">
                      {sample.skuCode} • PO Qty: {sample.poQuantity}
                    </p>
                        </div>
                  <Badge variant="outline">Sample {index + 1}</Badge>
                      </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                        <div>
                    <Label className="text-sm font-medium">Sample Size</Label>
                          <Input
                            type="number"
                      value={sample.sampleSize}
                      onChange={(e) => updateSampleSize(index, parseInt(e.target.value) || 1)}
                      min="1"
                      max={sample.poQuantity}
                      className="w-full"
                      placeholder="Enter sample size"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Recommended: {Math.max(1, Math.ceil(sample.poQuantity * 0.1))} (10% of {sample.poQuantity})
                    </p>
                        </div>
                        <div>
                    <Label className="text-sm font-medium">Quality Rating (0-5)</Label>
                    <div className="space-y-2">
                          <Input
                            type="number"
                        value={sample.rating || ''}
                        onChange={(e) => updateSampleRating(index, parseFloat(e.target.value) || 0)}
                            min="0"
                        max="5"
                        step="0.1"
                        placeholder="Enter rating (0.0 - 5.0)"
                        className="w-full"
                      />
                      <div className="flex items-center justify-center gap-1">
                        {[...Array(5)].map((_, i) => (
                          <Star 
                            key={i} 
                            className={`h-4 w-4 ${i < Math.floor(sample.rating) ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} 
                          />
                        ))}
                        <span className="ml-2 text-sm font-medium">
                          {sample.rating > 0 ? sample.rating.toFixed(1) : '0.0'}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Rate from 0.0 (Poor) to 5.0 (Excellent)
                      </p>
                    </div>
                  </div>
                      </div>

                      <div>
                        <Label htmlFor={`notes-${index}`}>Testing Notes</Label>
                        <Textarea
                          id={`notes-${index}`}
                    placeholder="Enter observations, defects, or quality notes for this sample..."
                    value={sample.notes}
                    onChange={(e) => updateSampleNotes(index, e.target.value)}
                          rows={2}
                        />
                      </div>

                {/* Sample Attachments */}
                <div>
                  <Label className="text-sm font-medium">Sample Images</Label>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        id={`sample-attachments-${index}`}
                        accept="image/*"
                        multiple
                        onChange={(e) => handleSampleFileUpload(index, e)}
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => document.getElementById(`sample-attachments-${index}`)?.click()}
                        className="flex items-center gap-2"
                      >
                        <Upload className="h-4 w-4" />
                        Upload Images
                      </Button>
                      <span className="text-xs text-muted-foreground">
                        Upload photos of defects, quality issues, or sample conditions
                      </span>
                    </div>
                    
                    {sample.attachments.length > 0 && (
                      <div className="space-y-1">
                        {sample.attachments.map((attachment, attachmentIndex) => (
                          <div key={attachmentIndex} className="flex items-center gap-2 p-2 bg-muted rounded-md">
                            <Image className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm flex-1">{attachment}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeSampleAttachment(index, attachmentIndex)}
                              className="h-6 w-6 p-0"
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                          </div>
                        )}
                      </div>
              </div>
            </CardContent>
          </Card>
          ))}
        </div>

        {/* Overall Results */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
              <Calculator className="h-4 w-4" />
              Overall IQC Results
              </CardTitle>
            </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <Label className="text-sm font-medium">Average Rating</Label>
                <div className="text-2xl font-bold">{overallRating.toFixed(2)}</div>
              </div>
              <div className="text-center">
                <Label className="text-sm font-medium">Status</Label>
                <Badge className={statusInfo.color}>
                  {statusInfo.icon}
                  {statusInfo.status}
                </Badge>
              </div>
              <div className="text-center">
                <Label className="text-sm font-medium">Samples Tested</Label>
                <div className="text-2xl font-bold">{samples.filter(s => s.rating > 0).length}/{samples.length}</div>
              </div>
            </div>
            
            <div>
              <Label htmlFor="overall-notes">Overall Testing Notes</Label>
              <Textarea
                id="overall-notes"
                placeholder="Enter overall observations and conclusions..."
                value={overallNotes}
                onChange={(e) => setOverallNotes(e.target.value)}
                rows={3}
              />
            </div>

            {/* Overall Attachments */}
                <div>
              <Label className="text-sm font-medium">Overall Test Images</Label>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    id="overall-attachments"
                    accept="image/*"
                    multiple
                    onChange={handleOverallFileUpload}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => document.getElementById('overall-attachments')?.click()}
                    className="flex items-center gap-2"
                  >
                    <Paperclip className="h-4 w-4" />
                    Upload Overall Images
                  </Button>
                  <span className="text-xs text-muted-foreground">
                    Upload general photos of the testing process, setup, or overall conditions
                  </span>
                </div>
                
                {overallAttachments.length > 0 && (
                  <div className="space-y-1">
                    {overallAttachments.map((attachment, attachmentIndex) => (
                      <div key={attachmentIndex} className="flex items-center gap-2 p-2 bg-muted rounded-md">
                        <Image className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm flex-1">{attachment}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeOverallAttachment(attachmentIndex)}
                          className="h-6 w-6 p-0"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                </div>
              </div>
            </CardContent>
          </Card>

        </div>
        {/* Fixed Footer */}
        <div className="shrink-0 border-t bg-background p-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            disabled={isSubmitting || samples.some(s => s.rating === 0 || s.rating === undefined || s.rating === null)}
            className="flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Submitting...
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4" />
                Complete IQC Testing
              </>
            )}
          </Button>
        </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};